// Centralized Facebook Pixel utility
// - Queues events if fbq isn't ready yet (no lost events on early clicks)
// - Flushes the queue when fbq finishes loading
// - trackAndNavigate ensures the pixel request fires before leaving the page

declare global {
  interface Window {
    fbq?: (...args: any[]) => void
    _fbq?: any
    __fbqQueue?: Array<[string, string, Record<string, any> | undefined]>
  }
}

export const FB_PIXEL_ID = '1215304437119773'

// Initialize an in-memory queue. Events fired before fbq loads land here.
if (typeof window !== 'undefined' && !window.__fbqQueue) {
  window.__fbqQueue = []
}

/**
 * Track a Facebook Pixel event. Safe to call before the pixel has loaded —
 * the event is queued and replayed once fbq is available.
 */
export function trackPixel(
  event: string,
  data?: Record<string, any>,
  customEvent = false,
) {
  if (typeof window === 'undefined') return
  const method = customEvent ? 'trackCustom' : 'track'
  if (window.fbq) {
    try {
      window.fbq(method, event, data)
    } catch {
      /* swallow — pixel issues should never break the page */
    }
  } else {
    // Queue for later flush
    window.__fbqQueue!.push([method, event, data])
  }
}

/**
 * Flush any queued events. Called automatically once the pixel finishes loading.
 */
export function flushPixelQueue() {
  if (typeof window === 'undefined' || !window.fbq || !window.__fbqQueue) return
  const queue = window.__fbqQueue
  window.__fbqQueue = []
  queue.forEach(([method, event, data]) => {
    try {
      window.fbq!(method, event, data)
    } catch {
      /* ignore */
    }
  })
}

/**
 * Fire a pixel event and then navigate to the given URL.
 * Gives the pixel ~250ms to send the beacon before navigating, so the event
 * doesn't get killed when the browser starts unloading the page.
 */
export function trackAndNavigate(
  event: string,
  data: Record<string, any> | undefined,
  href: string,
) {
  trackPixel(event, data)
  // Tiny delay so the network request leaves the browser before unload.
  // 250ms is the standard recommended delay for fb pixel + navigation.
  setTimeout(() => {
    window.location.href = href
  }, 250)
}
